import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { authAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { registerPush } from '../utils/push';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const SetPassword = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordRules = [
    { test: (p) => p.length >= 8, label: 'At least 8 characters' },
    { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
    { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
    { test: (p) => /[0-9]/.test(p), label: 'One number' },
    { test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`]/.test(p), label: 'One special character' },
  ];

  const allRulesPass = passwordRules.every((r) => r.test(newPassword));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesPass) {
      toast.error('Password does not meet all requirements');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.setPassword(newPassword);
      const newStatus = response.data.account_status;
      setUser({ ...user, account_status: newStatus });
      toast.success(response.data.message || 'Password set successfully');
      if (newStatus === 'active') {
        registerPush();
        navigate('/dashboard');
      } else {
        navigate('/pending-approval');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="flex items-center justify-center w-14 h-14 bg-green-50 rounded-full mx-auto mb-6">
            <Lock className="w-7 h-7 text-green-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 text-center" data-testid="set-password-title">
            Set Your Password
          </h1>
          <p className="mt-2 text-sm text-slate-500 text-center">
            Welcome! Please create a new password to secure your account.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
              <Input
                type="password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter a strong password"
                required
              />
              {newPassword.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {passwordRules.map((rule, i) => (
                    <li key={i} className={`flex items-center gap-1.5 text-xs ${rule.test(newPassword) ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${rule.test(newPassword) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                      {rule.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
              <Input
                type="password"
                data-testid="confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !allRulesPass}
              data-testid="set-password-btn"
              className="w-full h-12 rounded-full bg-green-700 hover:bg-green-800"
            >
              {loading ? 'Saving...' : 'Set Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
